import React, { useContext, useState, useEffect, useRef, useLayoutEffect, useMemo, useCallback } from 'react';
import { Button, Card, CardHeader, CardBody } from 'src/components/ui';
import PopoutWindow, { PanelHeader, getToolbarColor } from './PopoutWindow';
import { UWEThemeGlobals } from '../context/UWEThemeContext';
import { AuthContext } from '../context/AuthContext';
import { useLingui } from '@lingui/react/macro';
import ModalContext from '../context/ModalContext';
import UWEFormContext from '../context/UWEFormContext';
import { ConflictsSidebarPanel } from './ConflictsPanel';
import { evalShowIf } from '../components/customFields/HideObjectField';

import UWEEntityAssignments from './UWEEntityAssignments';
import UWEEntityHistory from './UWEEntityHistory';
import UWEEntityNote from './UWEEntityNote';
import UWEEntityProgress from './UWEEntityProgress';
import UWEEntityDataRemarks from './UWEEntityDataRemarks';
import classnames from 'src/util/classnames';
import { groupBy } from 'lodash';
import { getTestId } from '../hooks/useTestId';
import FormStatusBar from '../components/FormStatusBar';
import FormGoToLinks from '../components/FormGoToLinks';
import Icon from 'src/components/Icon';

const SIDEBAR_COMPONENTS = {
  assignments: UWEEntityAssignments,
  notes: UWEEntityNote,
  text: NewlineText,
  history: UWEEntityHistory,
  progress: UWEEntityProgress,
  dataremarks: UWEEntityDataRemarks,
  conflicts: ConflictsSidebarPanel,
};

function VResizer({ onResize, currentWidth }) {
  const ref = useRef(null);
  const widthRef = useRef(currentWidth);
  const onResizeRef = useRef(onResize);
  useEffect(() => {
    widthRef.current = currentWidth;
  }, [currentWidth]);
  useEffect(() => {
    onResizeRef.current = onResize;
  }, [onResize]);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    let dragging = false,
      startX = 0,
      startW = 0;

    const onDown = e => {
      dragging = true;
      startX = e.clientX;
      startW = widthRef.current;
      el.classList.add('sp-resizer-active');
      e.preventDefault();
    };
    const onMove = e => {
      if (!dragging) return;
      onResizeRef.current(Math.max(200, Math.min(700, startW - (e.clientX - startX))));
    };
    const onUp = () => {
      if (!dragging) return;
      dragging = false;
      el.classList.remove('sp-resizer-active');
    };

    el.addEventListener('mousedown', onDown);
    document.addEventListener('mousemove', onMove);
    document.addEventListener('mouseup', onUp);
    return () => {
      el.removeEventListener('mousedown', onDown);
      document.removeEventListener('mousemove', onMove);
      document.removeEventListener('mouseup', onUp);
    };
  }, []); // stable — callbacks accessed via refs

  return <div ref={ref} className="sp-v-resizer" />;
}

function HResizer({ onResize, currentHeight }) {
  const ref = useRef(null);
  const heightRef = useRef(currentHeight);
  const onResizeRef = useRef(onResize);
  useEffect(() => {
    heightRef.current = currentHeight;
  }, [currentHeight]);
  useEffect(() => {
    onResizeRef.current = onResize;
  }, [onResize]);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    let dragging = false,
      startY = 0,
      startH = 0;

    const onDown = e => {
      dragging = true;
      startY = e.clientY;
      startH = heightRef.current;
      el.classList.add('sp-resizer-active');
      e.preventDefault();
    };
    const onMove = e => {
      if (!dragging) return;
      const maxH =
        window.innerHeight - el.getBoundingClientRect().top - parseInt(getComputedStyle(el).getPropertyValue('--form-footer-height') || '0', 10);
      onResizeRef.current(Math.max(100, Math.min(maxH, startH - (e.clientY - startY))));
    };
    const onUp = () => {
      if (!dragging) return;
      dragging = false;
      el.classList.remove('sp-resizer-active');
    };

    el.addEventListener('mousedown', onDown);
    document.addEventListener('mousemove', onMove);
    document.addEventListener('mouseup', onUp);
    return () => {
      el.removeEventListener('mousedown', onDown);
      document.removeEventListener('mousemove', onMove);
      document.removeEventListener('mouseup', onUp);
    };
  }, []); // stable — callbacks accessed via refs

  return <div ref={ref} className="sp-h-resizer" />;
}

function FloatPanel({ title, panelMode, onSetMode, onClose, toolbarColor, pos, size, onPosChange, onSizeChange, children }) {
  const headerRef = useRef(null);
  const gripRef = useRef(null);
  const posRef = useRef(pos);
  const sizeRef = useRef(size);
  const onPosChangeRef = useRef(onPosChange);
  const onSizeChangeRef = useRef(onSizeChange);

  useEffect(() => {
    posRef.current = pos;
  }, [pos]);
  useEffect(() => {
    sizeRef.current = size;
  }, [size]);
  useEffect(() => {
    onPosChangeRef.current = onPosChange;
  }, [onPosChange]);
  useEffect(() => {
    onSizeChangeRef.current = onSizeChange;
  }, [onSizeChange]);

  useEffect(() => {
    const header = headerRef.current;
    const grip = gripRef.current;
    if (!header || !grip) return;

    let fDrag = false,
      fStartX = 0,
      fStartY = 0,
      fLeft = 0,
      fTop = 0;
    let rDrag = false,
      rStartX = 0,
      rStartY = 0,
      rStartW = 0,
      rStartH = 0;

    const onHdrDown = e => {
      if (e.target.closest('[data-sp-btn]')) return;
      fDrag = true;
      fStartX = e.clientX;
      fStartY = e.clientY;
      fLeft = posRef.current.x;
      fTop = posRef.current.y;
      e.preventDefault();
    };
    const onGripDown = e => {
      rDrag = true;
      rStartX = e.clientX;
      rStartY = e.clientY;
      rStartW = sizeRef.current.w;
      rStartH = sizeRef.current.h;
      e.preventDefault();
    };
    const onMove = e => {
      if (fDrag) {
        onPosChangeRef.current({ x: fLeft + (e.clientX - fStartX), y: fTop + (e.clientY - fStartY) });
      }
      if (rDrag) {
        onSizeChangeRef.current({
          w: Math.max(240, rStartW + (e.clientX - rStartX)),
          h: Math.max(160, rStartH + (e.clientY - rStartY)),
        });
      }
    };
    const onUp = () => {
      fDrag = false;
      rDrag = false;
    };

    header.addEventListener('mousedown', onHdrDown);
    grip.addEventListener('mousedown', onGripDown);
    document.addEventListener('mousemove', onMove);
    document.addEventListener('mouseup', onUp);
    return () => {
      header.removeEventListener('mousedown', onHdrDown);
      grip.removeEventListener('mousedown', onGripDown);
      document.removeEventListener('mousemove', onMove);
      document.removeEventListener('mouseup', onUp);
    };
  }, []); // stable — callbacks accessed via refs

  return (
    <div className="sp-float-modal" style={{ left: pos.x, top: pos.y, width: size.w, height: size.h }}>
      <PanelHeader
        headerRef={headerRef}
        style={{ cursor: 'move', flexShrink: 0 }}
        title={title}
        panelMode={panelMode}
        onSetMode={onSetMode}
        onClose={onClose}
        toolbarColor={toolbarColor}
      />
      <div className="sp-panel-content">{children}</div>
      <div ref={gripRef} className="sp-resize-grip" />
    </div>
  );
}

const SP_KEY = 'sp-panel-state';

function spLoad() {
  try {
    return JSON.parse(localStorage.getItem(SP_KEY) || '{}');
  } catch {
    return {};
  }
}

function spSave(key, value) {
  try {
    const d = spLoad();
    localStorage.setItem(SP_KEY, JSON.stringify({ ...d, [key]: value }));
  } catch {}
}

function SidebarComponents(props) {
  const { children, title, currentEntity, readOnly, hideSideTabs, wfAccessToken, formSchema, formUiSchema } = props;
  const propsComponents = props.onComponents || [];
  const auth = useContext(AuthContext);
  const uweFormContext = useContext(UWEFormContext);
  const { t } = useLingui();

  const SIDEBAR_TITLES = {
    assignments: t`Assignments`,
    notes: t`Notes`,
    text: t`Text`,
    history: t`History`,
    progress: t`Progress`,
    dataremarks: t`Flags`,
    conflicts: t`Conflicts`,
  };

  // Optional per-item JNX 'showIf', evaluated against the last loaded/saved entity (not live
  // in-progress edits — SidebarComponents wraps the form and has no access to its live formObject).
  const visibleComponents = propsComponents.filter(
    pc => !pc.showIf || evalShowIf(currentEntity, '', pc.showIf, { auth, formObject: currentEntity, currentEntity })
  );

  // Route all 'bottom' position components to 'right', keeping original position tag.
  // Overlay a translated title keyed by component type so tab labels update on locale change.
  const normalizedComponents = visibleComponents.map(pc => ({
    ...pc,
    title: SIDEBAR_TITLES[pc.type] ?? pc.title,
    _origPosition: pc.position,
    position: pc.position === 'bottom' ? 'right' : pc.position,
  }));

  const { left = [], right: _rightRaw = [] } = groupBy(normalizedComponents, x => x.position);
  // Originally-bottom components go at the end of the right tab list
  const right = [..._rightRaw.filter(pc => pc._origPosition !== 'bottom'), ..._rightRaw.filter(pc => pc._origPosition === 'bottom')];

  const hasUnresolvedDataConflicts = useMemo(() => {
    const dc = currentEntity?.data?.dataConflicts ?? {};
    return Object.values(dc).some(c => c != null && c !== false && c.resolved == null);
  }, [currentEntity?.data?.dataConflicts]);

  const hasUnresolvedDataRemarks = useMemo(() => {
    return (uweFormContext?.dataRemarks ?? []).some(r => !r.isResolved);
  }, [uweFormContext?.dataRemarks]);

  const showConflictsTab = !readOnly && currentEntity?.data?.reportingWindow != null && hasUnresolvedDataConflicts;
  const conflictsTabDef = showConflictsTab
    ? { mainContent: '__conflicts__', title: SIDEBAR_TITLES.conflicts, type: 'conflicts', color: 'warning', _origPosition: 'right' }
    : null;

  const rightTabs = conflictsTabDef ? [...right, conflictsTabDef] : right;

  const statusBar = formUiSchema?.['ui:statusBar'] ?? formUiSchema?.['statusBar'];
  const goToLinks = formUiSchema?.['ui:goToLinks'] ?? formUiSchema?.['goToLinks'];

  // Left panel open/closed state (existing behavior)
  const [leftComponents, setLeftComponents] = useState({});
  useEffect(() => {
    setLeftComponents(
      left.reduce((acc, pc) => {
        acc[pc.mainContent] = { open: false };
        return acc;
      }, {})
    );
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const setLeftComponent = (mainContent, open) => {
    setLeftComponents(prev => ({ ...prev, [mainContent]: { open } }));
  };
  const showLeft = Object.values(leftComponents).filter(x => x.open).length;

  // Right panel state — resizable, mode-switchable, persisted to localStorage
  const _saved = useRef(spLoad()).current;
  const [activeTab, setActiveTab] = useState(null);
  // 'popout' is not restored on reload — can't auto-open a popup window on mount
  const [panelMode, setPanelMode] = useState(_saved.panelMode === 'popout' ? 'right' : _saved.panelMode || 'right');
  const [rightWidth, setRightWidth] = useState(_saved.rightWidth || 340);
  const [bottomHeight, setBottomHeight] = useState(_saved.bottomHeight || Math.round(window.innerHeight * 0.25));
  const [floatPos, setFloatPos] = useState(_saved.floatPos || { x: 200, y: 160 });
  const [floatSize, setFloatSize] = useState(_saved.floatSize || { w: 380, h: 320 });

  // Auto-open the dataremarks tab once, on initial load, if the entity has unresolved remarks.
  // Guarded by entity id so it re-evaluates if currentEntity ever changes post-mount, without
  // fighting a tab the user already opened manually while remarks were still loading.
  const autoOpenedRemarksForEntityRef = useRef(null);
  useEffect(() => {
    if (hideSideTabs || uweFormContext?.loadingDataRemarks || !currentEntity?.id) return;
    if (autoOpenedRemarksForEntityRef.current === currentEntity.id) return;
    autoOpenedRemarksForEntityRef.current = currentEntity.id;
    if (hasUnresolvedDataRemarks && rightTabs.some(tab => tab.mainContent === 'dataremarks')) {
      setActiveTab(prev => prev ?? 'dataremarks');
    }
  }, [currentEntity?.id, uweFormContext?.loadingDataRemarks, hasUnresolvedDataRemarks, hideSideTabs, rightTabs]);

  // Persist panelMode immediately; persist resize values with debounce
  useEffect(() => {
    spSave('panelMode', panelMode);
  }, [panelMode]);
  const _rwTimer = useRef(null);
  const _bhTimer = useRef(null);
  const _fpTimer = useRef(null);
  const _fsTimer = useRef(null);
  const setRightWidthP = w => {
    setRightWidth(w);
    clearTimeout(_rwTimer.current);
    _rwTimer.current = setTimeout(() => spSave('rightWidth', w), 400);
  };
  const setBottomHeightP = h => {
    setBottomHeight(h);
    clearTimeout(_bhTimer.current);
    _bhTimer.current = setTimeout(() => spSave('bottomHeight', h), 400);
  };
  const setFloatPosP = p => {
    setFloatPos(p);
    clearTimeout(_fpTimer.current);
    _fpTimer.current = setTimeout(() => spSave('floatPos', p), 400);
  };
  const setFloatSizeP = s => {
    setFloatSize(s);
    clearTimeout(_fsTimer.current);
    _fsTimer.current = setTimeout(() => spSave('floatSize', s), 400);
  };

  // Persist dataremarks filter/selection state across panel mode switches
  const drStateRef = useRef(null);
  const handleDrStateChange = useCallback(state => {
    drStateRef.current = state;
  }, []);

  // Left panels have no panel layout mode; UWEEntityDataRemarks then falls back to its 'right' layout.
  const dataRemarksProps = (def, layout) =>
    def.type === 'dataremarks'
      ? {
          formSchema,
          formUiSchema,
          mode: def.mode,
          panelMode: layout,
          initialFilterState: drStateRef.current,
          onFilterStateChange: handleDrStateChange,
        }
      : {};

  const formContainerRef = useRef(null);
  const stickyHeaderRef = useRef(null);

  useLayoutEffect(() => {
    const header = stickyHeaderRef.current;
    const container = formContainerRef.current;
    if (!header || !container) return;
    const observer = new ResizeObserver(entries => {
      const height = entries[0]?.contentRect.height ?? 0;
      container.style.setProperty('--form-sticky-header-height', `${height}px`);
    });
    observer.observe(header);
    return () => observer.disconnect();
  }, []);

  const activeTabDef = rightTabs.find(tab => tab.mainContent === activeTab) ?? null;
  const ActiveComponent = activeTabDef ? SIDEBAR_COMPONENTS[activeTabDef.type] : null;
  const tabColor = getToolbarColor(activeTabDef);

  const popoutWinRef = useRef(null);

  const toggleTab = mainContent => {
    if (panelMode === 'popout' && panelOpen && popoutWinRef.current && !popoutWinRef.current.closed) {
      setActiveTab(mainContent);
      popoutWinRef.current.focus();
    } else {
      setActiveTab(prev => (prev === mainContent ? null : mainContent));
    }
  };
  const closePanel = () => setActiveTab(null);

  const panelContent =
    activeTabDef && ActiveComponent && currentEntity ? (
      <ActiveComponent
        auth={auth}
        text={activeTabDef.content}
        def={activeTabDef}
        entity={currentEntity}
        readOnly={readOnly}
        wfAccessToken={wfAccessToken}
        {...dataRemarksProps(activeTabDef, panelMode)}
      />
    ) : null;

  const panelOpen = !hideSideTabs && !!activeTab;
  const hasRightTabs = !hideSideTabs && rightTabs.length > 0;

  return children ? (
    <ModalContext.ProviderComponent>
      <div className="form-container">
        {/* Left panel — behavior unchanged */}
        {hideSideTabs || left.length === 0 ? null : (
          <div className={classnames('sidepanel', 'sidepanel-left', { expanded: showLeft })}>
            {left.map((obj, idx) => {
              const Component = SIDEBAR_COMPONENTS[obj.type];
              return currentEntity ? (
                <ExpandableTabSidePanel
                  key={idx}
                  title={obj.title}
                  expanded={leftComponents[obj.mainContent]?.open ? 1 : 0}
                  setExpanded={val => setLeftComponent(obj.mainContent, !!val)}
                >
                  <Component
                    auth={auth}
                    text={obj.content}
                    def={obj}
                    entity={currentEntity}
                    readOnly={readOnly}
                    wfAccessToken={wfAccessToken}
                    {...dataRemarksProps(obj)}
                  />
                </ExpandableTabSidePanel>
              ) : null;
            })}
          </div>
        )}

        {/* Main workspace: form + resizable right/bottom panel + side tabs */}
        <div className={classnames('sp-workspace', { 'sp-right-active': panelOpen && panelMode === 'right' })}>
          <div className={classnames('sp-form-wrap', { 'sp-bottom-active': panelOpen && panelMode === 'bottom' })}>
            <div className={classnames('form-component', 'uweentity-form', 'uweentityForm', props.formContainerClassName)} ref={formContainerRef}>
              <div className="form-sticky-header" ref={stickyHeaderRef}>
                {statusBar != null ? (
                  <FormStatusBar statusBar={statusBar} entity={currentEntity} />
                ) : (
                  <div
                    className={classnames('uweentity-title', {
                      'px-4': hideSideTabs || left.length === 0,
                    })}
                  >
                    {title}
                  </div>
                )}
                {goToLinks != null && (
                  <FormGoToLinks
                    goToLinks={goToLinks}
                    formFieldsByPath={uweFormContext?.formFieldsByPath}
                    navigateToField={uweFormContext?.navigateToField}
                  />
                )}
              </div>
              <div className="sp-form-body">
                <div
                  className={classnames('form-component-fields', {
                    // 'px-4': hideSideTabs || left.length === 0,
                  })}
                >
                  {children}
                </div>

                {/* Tab strip — to the right of form content, below the sticky header */}
                {hasRightTabs && (
                  <div className="sp-side-tabs sidepanel-right">
                    <div className="sp-side-tabs-inner">
                      {rightTabs.map((tab, idx) => {
                        const color = getToolbarColor(tab);
                        return (
                          <button
                            key={idx}
                            className={classnames('sp-side-tab', `tab-${color}`, {
                              [`active-${color}`]: activeTab === tab.mainContent,
                            })}
                            onClick={() => toggleTab(tab.mainContent)}
                            {...getTestId('sidebar', '.', 'tab', '-', tab.mainContent)}
                          >
                            {tab.title}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Bottom split panel */}
            {panelOpen && panelMode === 'bottom' && (
              <>
                <HResizer onResize={setBottomHeightP} currentHeight={bottomHeight} />
                <div className="sp-bottom-panel" style={{ height: bottomHeight }}>
                  <PanelHeader
                    title={activeTabDef.title}
                    panelMode={panelMode}
                    onSetMode={setPanelMode}
                    onClose={closePanel}
                    toolbarColor={tabColor}
                  />
                  <div className="sp-panel-content">{panelContent}</div>
                </div>
              </>
            )}
          </div>

          {/* Vertical resizer — shown when right-split is active */}
          {panelOpen && panelMode === 'right' && <VResizer onResize={setRightWidthP} currentWidth={rightWidth} />}

          {/* Right split panel */}
          {panelOpen && panelMode === 'right' && (
            <div className="sp-side-panel" style={{ width: rightWidth }}>
              <PanelHeader title={activeTabDef.title} panelMode={panelMode} onSetMode={setPanelMode} onClose={closePanel} toolbarColor={tabColor} />
              <div className="sp-panel-content">{panelContent}</div>
            </div>
          )}
        </div>

        {/* Floating modal — position: fixed, sits over everything */}
        {panelOpen && panelMode === 'float' && (
          <FloatPanel
            title={activeTabDef.title}
            panelMode={panelMode}
            onSetMode={setPanelMode}
            onClose={closePanel}
            toolbarColor={tabColor}
            pos={floatPos}
            size={floatSize}
            onPosChange={setFloatPosP}
            onSizeChange={setFloatSizeP}
          >
            {panelContent}
          </FloatPanel>
        )}

        {/* Popout — renders into a separate browser window via portal */}
        {panelOpen && panelMode === 'popout' && (
          <PopoutWindow
            title={activeTabDef.title}
            panelMode={panelMode}
            onSetMode={setPanelMode}
            onClose={closePanel}
            toolbarColor={tabColor}
            tabs={rightTabs}
            activeTab={activeTab}
            onTabChange={toggleTab}
            onWindowReady={win => {
              popoutWinRef.current = win;
            }}
            globalStyles={<UWEThemeGlobals />}
          >
            {panelContent}
          </PopoutWindow>
        )}
      </div>
    </ModalContext.ProviderComponent>
  ) : null;
}

function ExpandableTabSidePanel({ title, children, expanded, setExpanded }) {
  const { t } = useLingui();
  return expanded ? (
    <Card className="expandable-tab-side-panel-panel animated-show">
      <CardHeader>
        {title}
        <div className="btn-bar">
          <Button
            style={{ borderColor: 'transparent' }}
            onClick={() => setExpanded(0)}
            title={t`Close ${title}`}
            {...getTestId('sidebar', '.', 'left-close', '-', title.toLowerCase().replace(/\s+/g, '-'))}
          >
            <Icon icon="X" weight="regular" />
          </Button>
        </div>
      </CardHeader>
      <CardBody>{children}</CardBody>
    </Card>
  ) : (
    <button
      className="expandable-tab-side-panel-tab"
      onClick={() => setExpanded(1)}
      title={title}
      {...getTestId('sidebar', '.', 'left-open', '-', title.toLowerCase().replace(/\s+/g, '-'))}
    >
      {title}
    </button>
  );
}

function NewlineText({ text }) {
  return text.split('\n').map((str, idx) => <p key={idx}>{str}</p>);
}

export default SidebarComponents;
